/** Small anchored menu/popover: closes on outside click and Escape, returns focus. */
import { useEffect, useId, useRef, useState, type ReactNode } from 'react';
import { cx } from './core';

export function Popover({ trigger, children, align = 'left', className, label }: { trigger: (p: { open: boolean; toggle: () => void; id: string }) => ReactNode; children: (close: () => void) => ReactNode; align?: 'left' | 'right'; className?: string; label?: string }) {
  const [open, setOpen] = useState(false);
  const wrap = useRef<HTMLDivElement>(null);
  const id = useId();
  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (!wrap.current?.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setOpen(false);
        (wrap.current?.querySelector('[aria-haspopup]') as HTMLElement | null)?.focus();
      }
    };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);
  return (
    <div ref={wrap} className="relative">
      {trigger({ open, toggle: () => setOpen((o) => !o), id })}
      {open && (
        <div id={id} role="menu" aria-label={label} className={cx('absolute z-40 mt-1 min-w-[220px] rounded-tag border border-line bg-card p-1 shadow-lift', align === 'right' ? 'right-0' : 'left-0', className)}>
          {children(() => setOpen(false))}
        </div>
      )}
    </div>
  );
}

export function MenuItem({ children, onClick, icon, danger, checked, href }: { children: ReactNode; onClick?: () => void; icon?: ReactNode; danger?: boolean; checked?: boolean; href?: string }) {
  const cls = cx('flex min-h-touch w-full items-center gap-2.5 rounded-[4px] px-2.5 text-left text-sm hover:bg-sunken', danger && 'text-danger');
  const inner = (
    <>
      {icon && <span className="text-muted [&>svg]:h-4 [&>svg]:w-4">{icon}</span>}
      <span className="min-w-0 flex-1 truncate">{children}</span>
      {checked !== undefined && <span aria-hidden className={cx('h-4 w-4 rounded-[3px] border', checked ? 'border-brand bg-brand' : 'border-strong')} />}
    </>
  );
  if (href)
    return (
      <a role="menuitem" href={href} className={cls}>
        {inner}
      </a>
    );
  return (
    <button type="button" role={checked === undefined ? 'menuitem' : 'menuitemcheckbox'} aria-checked={checked} onClick={onClick} className={cls}>
      {inner}
    </button>
  );
}
